import { HttpClient, HttpEventType, HttpHeaders, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, throwError as observableThrowError, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EnvService } from 'src/app/core/deployment/env.service';
import { saveBlob } from 'src/app/shared/file-download/service/SaveData';
import { AlertService } from '../../alert/service/alert.service';
import { ERROR_PROGRESS, FileDownload } from './FileDownload';

@Injectable()
export class DownloadingService {
  private subject = new Subject<FileDownload>();

  constructor(
    private httpClient: HttpClient,
    private env: EnvService,
    private alertService: AlertService
  ) {}

  getFiles(): Observable<FileDownload> {
    return this.subject.asObservable();
  }

  downloadFileAsBlob(fileUrl: string, fileName: string, method: string = 'GET', body: string = ''): void {
    let headers = new HttpHeaders();

    if (body) {
      headers = headers.set('Content-Type', 'application/json');
    }
    const req = new HttpRequest(method, fileUrl, body, {
      reportProgress: true,
      responseType: 'blob',
      headers: headers,
    });

    const fileToDownload: FileDownload = {
      fileName: fileName,
      progress: -1,
    };

    this.httpClient
      .request(req)
      .pipe(
        catchError((err) => {
          fileToDownload.progress = ERROR_PROGRESS;
          this.subject.next(fileToDownload);
          this.alertService.error(`Error downloading file ${fileName}`);
          return observableThrowError(err);
        })
      )
      .subscribe((event) => {
        // Via this API, you get access to the raw event stream.
        // Look for download progress events.
        if (event.type === HttpEventType.DownloadProgress) {
          // This is an download progress event. Compute and show the % done:
          const percentDone = Math.round((100 * event.loaded) / event.total);
          fileToDownload.progress = percentDone ? percentDone : 100;
        } else if (event instanceof HttpResponse) {
          saveBlob(<Blob>event.body, fileName);
          fileToDownload.progress = 100;
          this.subject.next(fileToDownload);
        }
      });

    this.subject.next(fileToDownload);
  }

  downloadFileAsBlobWithError(fileUrl: string, fileName: string, method: string = 'GET', body: string = ''): void {
    let headers = new HttpHeaders();

    if (body) {
      headers = headers.set('Content-Type', 'application/json');
    }
    const req = new HttpRequest(method, fileUrl, body, {
      reportProgress: true,
      responseType: 'blob',
      headers: headers,
    });

    const fileToDownload: FileDownload = {
      fileName: fileName,
      progress: -1,
    };

    this.httpClient
      .request(req)
      .pipe(
        catchError((err) => {
          this.subject.next(fileToDownload);
          if (err.status === 409) {
            this.alertService.error(`Document cannot be generated because template is not excel format.`);
          } else {
            this.alertService.error(`Error downloading file ${fileName}`);
          }
          return observableThrowError(err);
        })
      )
      .subscribe((event) => {
        // Via this API, you get access to the raw event stream.
        // Look for download progress events.
        if (event.type === HttpEventType.DownloadProgress) {
          // This is an download progress event. Compute and show the % done:
          const percentDone = Math.round((100 * event.loaded) / event.total);
          fileToDownload.progress = percentDone ? percentDone : 100;
        } else if (event instanceof HttpResponse) {
          saveBlob(<Blob>event.body, fileName);
          fileToDownload.progress = 100;
          this.subject.next(fileToDownload);
        }
      });

    this.subject.next(fileToDownload);
  }

  async downloadFileInChunks(isProcess: boolean, fileId: number, fileName: string, totalChunks: number, method: string = 'GET', body: string = '') {
    const fileToDownload: FileDownload = {
      fileName: fileName,
      progress: -1,
    };

    this.subject.next(fileToDownload);
    const fileParts: Blob[] = [];
    let totalBytes = 0;

    try {
      for (let i = 0; i < totalChunks; i++) {
        const chunk = await firstValueFrom(this.downloadChunk(isProcess, i, fileId));
        totalBytes += chunk.size;
        const percentDone = Math.round((100 * totalBytes) / (chunk.size * totalChunks));
        fileToDownload.progress = percentDone;
        this.subject.next(fileToDownload);

        fileParts.push(chunk);
      }
      const file = new Blob(fileParts);
      saveBlob(file, fileName);
      fileToDownload.progress = 100;
      this.subject.next(fileToDownload);
    } catch (err) {
      fileToDownload.progress = 0;
      this.subject.next(fileToDownload);
      this.alertService.error(`Error downloading file ${fileName}`);
      return observableThrowError(err);
    } finally {
      this.subject.next(fileToDownload);
    }
  }

  private downloadChunk(isProcess: boolean, chunkNumber: number, id: number): Observable<Blob> {
    if (isProcess === true) {
      const url = `${this.env.server}/v1/Process/DownloadChunk?id=${id}&chunkNumber=${chunkNumber}`;
      return this.httpClient.get(url, { responseType: 'blob' });
    } else {
      const url = `${this.env.server}/v1/Download/DownloadChunk?templateProjectDownloadId=${id}&chunkNumber=${chunkNumber}`;

      return this.httpClient.get(url, { responseType: 'blob' });
    }
  }
}

function printDocument(doc) {
  // Wait until PDF is ready to print
  if (typeof doc.print === 'undefined') {
    setTimeout(function () {
      printDocument(doc);
    }, 1000);
  } else {
    doc.print();
  }
}
